import React, { createContext, useState, useContext, useEffect } from 'react';
import { useServerLogs } from './ServerLogContext';

// Create context
const ProgressContext = createContext(null);

export function ProgressProvider({ children }) {
  const [activeOperations, setActiveOperations] = useState([]);
  const [progressData, setProgressData] = useState({});
  const { isConnected, socket } = useServerLogs();

  // Handle progress events from Socket.IO
  useEffect(() => {
    if (!isConnected || !socket) return;

    const handleProgressStart = (data) => {
      const { operationId, operationType, totalTasks, description } = data;
      
      setActiveOperations(prev => [...prev, operationId]);
      setProgressData(prev => ({
        ...prev,
        [operationId]: {
          operationType,
          totalTasks,
          completedTasks: 0,
          currentTask: '',
          taskDescription: '',
          description,
          progress: 0,
          phase: 'setup',
          startTime: Date.now()
        }
      }));
    };

    const handleProgressUpdate = (data) => {
      const { operationId, completedTasks, currentTask, taskDescription, progress, phase } = data;
      
      setProgressData(prev => ({
        ...prev,
        [operationId]: {
          ...prev[operationId],
          completedTasks,
          currentTask,
          taskDescription,
          progress,
          phase
        }
      }));
    };

    const handleProgressComplete = (data) => {
      const { operationId, totalCompleted, success, message } = data;
      
      setProgressData(prev => ({
        ...prev,
        [operationId]: {
          ...prev[operationId],
          completedTasks: totalCompleted,
          progress: 100,
          success,
          message,
          completed: true,
          completedAt: Date.now()
        }
      }));

      // Remove from active operations after a delay
      setTimeout(() => {
        setActiveOperations(prev => prev.filter(id => id !== operationId));
        setProgressData(prev => {
          const newData = { ...prev };
          delete newData[operationId];
          return newData;
        });
      }, 3000); // Keep completed operations visible for 3 seconds
    };

    // Listen for progress events
    socket.on('progress-start', handleProgressStart);
    socket.on('progress-update', handleProgressUpdate);
    socket.on('progress-complete', handleProgressComplete);

    return () => {
      socket.off('progress-start', handleProgressStart);
      socket.off('progress-update', handleProgressUpdate);
      socket.off('progress-complete', handleProgressComplete);
    };
  }, [isConnected, socket]);

  // Get progress for a specific operation
  const getOperationProgress = (operationId) => {
    return progressData[operationId] || null;
  };

  // Get all active operations
  const getActiveOperations = () => {
    return activeOperations.map(id => ({
      id,
      ...progressData[id]
    })).filter(op => op && !op.completed);
  };

  // Get all operations (including recently completed)
  const getAllOperations = () => {
    return Object.entries(progressData).map(([id, data]) => ({
      id,
      ...data
    }));
  };

  // Clear completed operations
  const clearCompletedOperations = () => {
    setProgressData(prev => {
      const newData = {};
      Object.entries(prev).forEach(([id, data]) => {
        if (!data.completed) {
          newData[id] = data;
        }
      });
      return newData;
    });
  };

  // Check if any operations are active
  const hasActiveOperations = () => {
    return activeOperations.length > 0;
  };

  return (
    <ProgressContext.Provider value={{
      activeOperations,
      progressData,
      getOperationProgress,
      getActiveOperations,
      getAllOperations,
      clearCompletedOperations,
      hasActiveOperations
    }}>
      {children}
    </ProgressContext.Provider>
  );
}

// Custom hook to use the progress context
export function useProgress() {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
} 